import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { motion } from 'framer-motion';
import { ChevronLeft, Pause, Play } from 'lucide-react';
import type { ArchetypeId } from '@llura/types';

import { Button, Card, Slider } from '@llura/ui';

import { useCoachStore } from '../../stores/coach.store';
import { useStrings } from '../../hooks/useStrings';
import { useWakeLock } from '../../hooks/useWakeLock';
import { useArchetypeTheme } from '../../hooks/useArchetypeTheme';
import { isArchetypeId } from '../../lib/archetypes';
import { getAfterglowAudio, SAFE_EXIT_AFTERGLOW } from '../../lib/coach-audio';

const IDLE_REDIRECT_MS = 8000;

function fmt(seconds: number): string {
  if (!isFinite(seconds)) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Soft landing after a ritual — or after the safe-exit shortcut. Two minutes
 * of cooldown audio over a slow plum/rose aura, then two CTAs: open the
 * journal (capture the felt sense) or return to "your space".
 *
 * Safe-exit variant: header + body wording switches to a strictly calm,
 * medical register, and the audio source flips to the neutral cooldown so
 * the experience doesn't carry the in-character coach voice over a stop.
 *
 * After the cooldown audio ends we elevate the journal CTA visually and
 * start an 8s idle timer that auto-routes to the journal. Any pointer
 * activity resets the timer; the "Stay here" button cancels entirely.
 */
export function AfterglowPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { strings } = useStrings();
  const { gender, tone } = useCoachStore();

  // Keep the screen awake through the cooldown audio.
  useWakeLock();

  const isSafeExit = searchParams.get('reason') === 'safe-exit';
  const archetypeFromState = (location.state as { archetypeId?: string } | null)?.archetypeId;
  const archetypeId: ArchetypeId | null =
    !isSafeExit && archetypeFromState && isArchetypeId(archetypeFromState)
      ? archetypeFromState
      : null;
  const theme = useArchetypeTheme(archetypeId);

  const session = useMemo(
    () =>
      isSafeExit || !gender || !tone
        ? SAFE_EXIT_AFTERGLOW
        : getAfterglowAudio(gender, tone),
    [isSafeExit, gender, tone],
  );
  const totalSeconds = session.durationS;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fallbackIntervalRef = useRef<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [fallback, setFallback] = useState(false);
  const [audioEnded, setAudioEnded] = useState(false);
  const [idleCancelled, setIdleCancelled] = useState(false);
  const [idleRemaining, setIdleRemaining] = useState(IDLE_REDIRECT_MS);
  const idleStartRef = useRef<number | null>(null);

  useEffect(() => {
    if (!fallback) return undefined;
    if (!playing) {
      if (fallbackIntervalRef.current !== null) {
        window.clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
      return undefined;
    }
    fallbackIntervalRef.current = window.setInterval(() => {
      setPos((p) => {
        if (p >= totalSeconds) {
          setPlaying(false);
          setAudioEnded(true);
          return totalSeconds;
        }
        return p + 1;
      });
    }, 1000);
    return () => {
      if (fallbackIntervalRef.current !== null) {
        window.clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
    };
  }, [fallback, playing, totalSeconds]);

  const resetIdle = useCallback(() => {
    idleStartRef.current = performance.now();
    setIdleRemaining(IDLE_REDIRECT_MS);
  }, []);

  // Idle countdown — fires once audio has ended and the user hasn't opted out.
  useEffect(() => {
    if (!audioEnded || idleCancelled) return undefined;
    idleStartRef.current = performance.now();
    const tick = window.setInterval(() => {
      if (idleStartRef.current === null) return;
      const elapsed = performance.now() - idleStartRef.current;
      const remaining = Math.max(0, IDLE_REDIRECT_MS - elapsed);
      setIdleRemaining(remaining);
      if (remaining <= 0) {
        window.clearInterval(tick);
        navigate('/journal');
      }
    }, 100);
    const onActivity = () => resetIdle();
    window.addEventListener('pointermove', onActivity);
    window.addEventListener('pointerdown', onActivity);
    return () => {
      window.clearInterval(tick);
      window.removeEventListener('pointermove', onActivity);
      window.removeEventListener('pointerdown', onActivity);
    };
  }, [audioEnded, idleCancelled, navigate, resetIdle]);

  const togglePlay = () => {
    if (fallback) {
      setPlaying((p) => !p);
      return;
    }
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => {
        setFallback(true);
        setPlaying(true);
      });
    } else {
      el.pause();
    }
  };

  const seek = (next: number) => {
    setPos(next);
    const el = audioRef.current;
    if (el && !fallback) el.currentTime = next;
  };

  const pct = totalSeconds === 0 ? 0 : (pos / totalSeconds) * 100;
  const idlePct = (1 - idleRemaining / IDLE_REDIRECT_MS) * 100;

  const title = isSafeExit ? strings.afterglow.titleSafeExit : strings.afterglow.title;
  const hint = isSafeExit ? strings.afterglow.hintSafeExit : strings.afterglow.hint;

  return (
    <motion.div
      className="mx-auto flex w-full max-w-md flex-col gap-7 px-4 py-6"
      style={theme.style}
      data-archetype={theme.dataArchetype}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.5 }}
    >
      <header className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/dashboard')}
          className="rounded-full"
          aria-label={strings.ritual.backAria}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <p className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
          {strings.afterglow.eyebrow}
        </p>
      </header>

      <section className="space-y-3 text-center">
        <h1 className="font-serif text-3xl leading-tight text-foreground">{title}</h1>
        <p className="text-sm text-muted-foreground">{hint}</p>
      </section>

      <motion.div
        animate={{ opacity: audioEnded ? 0.45 : 1 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
      >
        <CoolDownAura />
      </motion.div>

      <Card className="space-y-4 border-border bg-card p-5">
        <Slider
          value={[pct]}
          max={100}
          step={0.1}
          onValueChange={(v) => seek((v[0] / 100) * totalSeconds)}
          aria-label={strings.levels.audio.seekAria}
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{fmt(pos)}</span>
          <span>{fmt(totalSeconds)}</span>
        </div>
        <div className="flex items-center justify-center">
          <Button
            size="icon"
            onClick={togglePlay}
            className="h-14 w-14 rounded-full bg-secondary text-secondary-foreground glow-secondary"
            aria-label={playing ? strings.levels.audio.pauseAria : strings.levels.audio.playAria}
          >
            {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 translate-x-0.5" />}
          </Button>
        </div>

        <audio
          ref={audioRef}
          src={session.url}
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
          onEnded={() => {
            setPlaying(false);
            setAudioEnded(true);
          }}
          onError={() => setFallback(true)}
        />
      </Card>

      <div className="flex flex-col items-center gap-2">
        {audioEnded && (
          <motion.p
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center text-xs text-muted-foreground"
          >
            {strings.afterglow.journalNudge}
          </motion.p>
        )}
        <motion.div
          className="relative w-full"
          animate={audioEnded ? { scale: 1.04 } : { scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <Button
            size="lg"
            onClick={() => navigate('/journal')}
            className="h-12 w-full rounded-full bg-primary text-primary-foreground glow-primary"
          >
            {strings.afterglow.journalCta}
          </Button>
          {audioEnded && !idleCancelled && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] origin-left rounded-full bg-accent/70"
              style={{ transform: `scaleX(${idlePct / 100})`, transition: 'transform 100ms linear' }}
            />
          )}
        </motion.div>
        {audioEnded && !idleCancelled && (
          <button
            type="button"
            onClick={() => setIdleCancelled(true)}
            className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            {strings.afterglow.stayHereCta}
          </button>
        )}
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="w-full text-sm text-muted-foreground hover:text-foreground"
        >
          {strings.afterglow.homeCta}
        </Button>
      </div>
    </motion.div>
  );
}

/** Slower, smaller cousin of the Your-Space aura — set to a breathing rhythm. */
function CoolDownAura() {
  return (
    <div className="relative mx-auto flex h-40 w-40 items-center justify-center">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          aria-hidden
          className="absolute inset-0 rounded-full border border-secondary/30"
          animate={{
            scale: [1 - i * 0.08, 1.05 - i * 0.08, 1 - i * 0.08],
            opacity: [0.25, 0.5, 0.25],
          }}
          transition={{
            duration: 7.5 + i * 0.6,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.5,
          }}
        />
      ))}
      <motion.div
        aria-hidden
        className="absolute inset-8 rounded-full bg-[radial-gradient(circle_at_50%_50%,hsl(var(--secondary)/0.35),hsl(var(--primary)/0.15)_60%,transparent_80%)] blur-md"
        animate={{ scale: [0.95, 1.08, 0.95], opacity: [0.45, 0.75, 0.45] }}
        transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
}
